import { defineComponent as _defineComponent } from 'vue'
import { store } from "@/store";
import QueryParamService, { QueryParams } from "@/core/query-params.service";
import { PurchaseCtaEnum } from "@/services/product.service";
  
import Dropdown from "@/ui/shared/Dropdown.vue";
import PDPSwiper from "./pdp-swiper/PDPSwiper.vue";
import ProductVariantSpec from "./ProductVariantSpec.vue";
import GetCutsheetLink from "./get-cutsheet/GetCutsheetLink.vue";
import TrackingService from "@/core/tracking.service";
import ProductPropertySelector from "./ProductPropertySelector.vue";
import {
    initializePrivateFilter,
    PrivatePrioritizedVariantFilterObject,
    ProductProperty,
    ProductPropertyData,
    ProductPropertyOption,
} from "./product-properties";
import { uniqBy } from "lodash";
import useBreakpoints from "@/core/responsive/breakpoints/breakpoint.mixin";
import ProductViewInAugmentedReality from "@/ui/views/product-details/ProductViewInAugmentedReality.vue";
import Vue, { computed, ref, watch, onMounted, onBeforeUnmount  } from "vue";

  
  declare global {
    // eslint-disable-next-line no-unused-vars
    interface Window {
      // eslint-disable-next-line no-unused-vars
      ExpiviComponent: (...args: any[]) => any;
    }
  }
  interface PrivateProductProperty {
    name: ProductProperty;
    description?: ProductProperty | undefined;
  }
  

export default /*#__PURE__*/_defineComponent({
  __name: 'ProductVariantPicker',
  setup(__props) {

/* global window, document */
const product = computed<ProductBaseViewModel<VariantBaseViewObject>>(() => store.getters["catalog/product"]);
const selectedVariant = computed<PrivateVariantViewObject>(() => store.getters["catalog/selectedVariant"]);
  
let ExpiviComponentInitialized: boolean = false;
onMounted(() => {
    if (selectedVariant.value.isConfigureable && !ExpiviComponentInitialized) {        
        ExpiviComponentInitialized=true;
        initializeExpiviComponent();
    }
});
  
// If product can change dynamically, also watch for changes:
watch(() => selectedVariant.value, (newSelectedVariant) => {
    if (newSelectedVariant.isConfigureable && !ExpiviComponentInitialized) {
        ExpiviComponentInitialized=true;
        setTimeout(initializeExpiviComponent,100);
    }
});
  
function initializeExpiviComponent() {
      
    const ColorSelectorViewDefinition = {
        props: {
            label: String,
            value: {
                default: null
            },
            items: Array,
            loading: {
                default: false
            },
            size: {
                type: String,
                default: "lg"
            },
            rounded: {
                type: String,
                default: "no"
            },
            node_id: Number
        },
        data: function() {
            return {
                selectedOption: null,
                intendedColorChangeText: null
            };
        },
        methods: {
            setInitialState() {
                this.selectedOption = this.items[0];
                this.intendedColorChangeText = this.stripColorCode(this.items[0].label);
            },
            selectedItem(item) {
                this.selectedOption = item;
                this.intendedColorChangeText = this.stripColorCode(item.label);
            },
            changeColor(item) {
                return item.color;
            },
            getColorOptionInnerStyles(item) {
                return item.thumbnail
                    ? { backgroundImage: `url('${item.thumbnail}')`, backgroundSize: "contain" }
                    : { backgroundColor: item.color };
            },
            setIntendedColorChangeText(item) {
                this.intendedColorChangeText = item ? this.stripColorCode(item.label) : "";
            },
            stripColorCode(colorText) {
                if (!colorText) return "";
                const pos = colorText.lastIndexOf(" ");
                if (pos > 0 && colorText.substring(pos+1).toLowerCase().startsWith("lp")) return colorText.substring(0, pos);
                return colorText;
            }          
        },
        template: `
      <div class="u-relative u-cursor-pointer u-py-5 u-border-b-1 u-border-black u-border-opacity-25">
          <div class="u-flex u-w-full">
              {{label}} 
              <Transition name="u-fade-100" mode="out-in">
                  <div class="u-ml-2 u-text-dark-5">
                      {{ intendedColorChangeText }}
                  </div>
              </Transition>
          </div>
          <div v-for="(item, index) in items"
              class="c-property-selector__option--color"
              :title="stripColorCode(item.label)"
              :key="index"
              @mouseover="setIntendedColorChangeText(item)"
              @mouseout="setIntendedColorChangeText(selectedOption)"
              @click="selectedItem(item)">
              <button class="c-property-selector-color"
                  :class="{ 'c-property-selector-color--active': selectedOption === item }"
                  :style="getColorOptionInnerStyles(item)">
              </button>
          </div>
      </div>
              `                
        ,
        watch: {
            selectedOption(aNewValue) {
                this.$emit("input", aNewValue.id);
            }
            ,items: {
                handler(newItems) {
                    if (newItems && newItems.length > 0) {
                        this.setInitialState(); // Call a method when items are populated
                    }
                },
                immediate: true, // Trigger the watcher immediately on component creation
                deep: true, // Watch for changes within the array (e.g., adding/removing items)
            },}  }
    ;
    
    ExpiviOptionsVueInstance.prototype.$customViews["housing-shade-one"] =Vue.component("customExpiviColorPicker1", { ...ColorSelectorViewDefinition });;
    ExpiviOptionsVueInstance.prototype.$customViews["shade-two"] = Vue.component("customExpiviColorPicker2", { ...ColorSelectorViewDefinition });
    ExpiviOptionsVueInstance.prototype.$customViews["shade-three"] = Vue.component("customExpiviColorPicker3", { ...ColorSelectorViewDefinition });
    ExpiviOptionsVueInstance.prototype.$customViews["shade-four"] = Vue.component("customExpiviColorPicker4", { ...ColorSelectorViewDefinition });
    ExpiviOptionsVueInstance.prototype.$customViews["spacers"] = Vue.component("customExpiviColorPicker5", { ...ColorSelectorViewDefinition });
    ExpiviOptionsVueInstance.prototype.$customViews["anti-glare-ring"] = Vue.component("customExpiviColorPicker6", { ...ColorSelectorViewDefinition });
    ExpiviOptionsVueInstance.prototype.$customViews["cord"] = Vue.component("customExpiviColorPicker7", { ...ColorSelectorViewDefinition });
    const expiviInitialization = {
        catalogueId: 25167,
        token:
        "eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiIxIiwianRpIjoiY2Y5YTMyOWZiYjVmMGM0MmMyNzQ1YzEzZGQxNzBjMTNmZmI1MjFmNThjYzQ4NjNiZWFkYTg2ZjI0OTk5MWIxZjUyZjMwY2I1YTdkNWUyMDAiLCJpYXQiOjE3NDY3MDg2MDcuOTM4NjI0LCJuYmYiOjE3NDY3MDg2MDcuOTM4NjI2LCJleHAiOjIwNjIyNDE0MDcuNzE5NDI4LCJzdWIiOiI2NDQ4Iiwic2NvcGVzIjpbXX0.o_MG4T4b3ew3PQlq8E7_7lBo1E5DefMkCELOyEm8IK6VZ5tJAnKlncigXCnwzsqTnqbG6GVkQHmejw-qRKTuSJqqKnzldErSPJd-IGgV6Aa5VpPfDeIFG90Df95TyNzFx1Vuvij4oTSORQsT_F8liriej7uXND4ce7O9HP2350BqVFEpq5l7Xc-ZwuZ0fGOTgMi5Ji2Y1CgpBxBmattuQaxeEdcHiKBu8D71vi0km6i3qhJW57vLjzwHRhtf7q_N0WvnXKX0gQvmSQNelOTjmeWojIaYCyg0GckT2itNLZmZJQzRhPa9Pzw6_SxN3SWpFUqIc7L6zWuoK6N2Qr_ODdjDOV-ug5KBkDMmYgYW_2GiCA8uLyPnvkJOjzPN8xAGSezsJaxoZopHMHvg_DeYZSJWspTKOwEo-olTtIyuzfGoeI9PQG203B7SXiu0EzFgjP5YwLljfentzVWcXKdAi7C02oszdRXuW6Qlup4_uJvw9kkwSyuqYT3VYlxRcxH7s8V3y0ihiLW-xEUg22StJjgtXjSJuSPOcNMsuJqzw2RddZCU6TiieAIQUFmXG8b3Zxlepfn8yP6Z2ve8nOGjxzO-hiizSFgZFU0g65nVNa3_3UHSCo43nmhbdSAltOsdfDUv28NK54QZ6iFAR5Cu-Bv_wYQ8EJS7j9KgWe4PGow",
        viewerContainer: document.getElementById("viewer-container"),
        optionContainer: document.getElementById("options-container"),
        priceSelectors: document.getElementById("price-container"),
    };
    window.ExpiviComponent(expiviInitialization);
}
  
const gaTrackingGoal = computed<string>(() => store.getters["catalog/gaTrackingGoal"]);
const findRetailersUrl = computed<string>(() => store.getters["app/findRetailersUrl"]);
const hasChangedRoute = computed<boolean>(() => store.getters["app/hasChangedRoute"]);
const theme = computed<ColorTheme>(() => store.getters["app/theme"]);
  
const breakpointIsActive = useBreakpoints();
  
function selectVariant(variant) {
    store.dispatch("catalog/selectVariant", variant);
}
function toggleShowVariantList(flag: boolean) {
    store.dispatch("catalog/toggleShowVariantList", flag);
}
  
const colors = ref<IColorOption[]>(getDistinctColors());
const options = ref<ProductPropertyData[]>([]);
  
const prioritizedVariantFilter = ref<PrivatePrioritizedVariantFilterObject>(initializePrivateFilter());
  
let hasTrackedVariant = false;
const productProperties: PrivateProductProperty[] = [
    {
        name: ProductProperty.propertyNameSizeText,
    },
    {
        name: ProductProperty.propertyNameLightsourceText,
    },
    {
        name: ProductProperty.propertyNameArmText,
    },
    {
        name: ProductProperty.propertyNameMountingText,
    },
    {
        name: ProductProperty.propertyNameCableType,
    },
    {
        name: ProductProperty.propertyNameSwitchPlug,
    },
    {
        name: ProductProperty.propertyNameShieldDiffusor,
    },
    {
        name: ProductProperty.propertyNameAdditionalFeatures,
        description: ProductProperty.propertyNameAdditionalFeaturesDescription,
    },
];
  
const hasSplashText = computed<boolean>(() => {
    return !!selectedVariant.value.splashText1 || !!selectedVariant.value.splashText2;
});
  
const getSplashIcon = computed<string>(() => {
    return selectedVariant.value.splash2Svg;
});
  
selectVariant(getVariantFromUrl() || getDefaultVariant());
  
watch(() => selectedVariant.value, onSelectedVariantChanged, { immediate: true });
function onSelectedVariantChanged(variant: VariantBaseViewObject) {
    if (selectedVariant.value.isConfigureable) return;
    prioritizedVariantFilter.value.sizeText.selectedValue = variant.sizeText;
    prioritizedVariantFilter.value.colorCode.selectedValue = variant.colorCode;
    prioritizedVariantFilter.value.lightsourceText.selectedValue = variant.lightsourceText;
    prioritizedVariantFilter.value.armText.selectedValue = variant.armText;
    prioritizedVariantFilter.value.mountingText.selectedValue = variant.mountingText;
    prioritizedVariantFilter.value.cableType.selectedValue = variant.cableType;
    prioritizedVariantFilter.value.switchPlug.selectedValue = variant.switchPlug;
    prioritizedVariantFilter.value.shieldDiffusor.selectedValue = variant.shieldDiffusor;
    prioritizedVariantFilter.value.additionalFeatures.selectedValue = variant.additionalFeatures;
  
    options.value = productProperties.map((property, index) => ({
        property: property.name,
        options: getDistinctVariantProperty(property.name, productProperties.map((y) => y.name).slice(0, index), property.description),
    }));
  
    trackVariant();
}
  
const showDeliveryTime = computed<boolean>(() => {
    return selectedVariant.value.showDeliveryTime;
});
  
const showReadyForShipment = computed<boolean>(() => {
    return selectedVariant.value.showReadyForShipment;
});
  
const showAddToBasketButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.AddToBasket as any);
});
  
const showFindDealerButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.FindDealer as any);
});
  
const showGetQuoteButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.GetQuote as any);
});
  
const showDiscontinuedText = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.Discontinued as any);
});
  
const showSoldOutText = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.SoldOut as any) && !!selectedVariant.value.soldOutText;
});
  
const showPricingB2BButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.ShowPricingB2B as any);
});
  
const showAddToBasketB2BButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.AddToBasketB2B as any);
});
  
const showGetQuoteB2CButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.GetQuoteB2C as any);
});
  
const productPackshotColor = computed<string>(() => {
    return !theme.value.productTileBackgroundColor ? product.value.backgroundColor : theme.value.productTileBackgroundColor;
});
  
function getDistinctColors() {
    const colorVariants = product.value.variants.filter((v: any) => !v.isConfigureable);
    return uniqBy(colorVariants, (v: any) => v.colorCode).map((v) => ({
        code: v.colorCode,
        image: v.colorImage,
        text: v.colorText,
    }));
}
  
function validOptions(propToCheck: string, checkAgainst: ProductProperty[]): string[] {
    return product.value.variants
        .filter((v) => {
            let isValid = true;
            [ProductProperty.propertyNameColorCode].concat(checkAgainst).forEach((prop) => {
                if (prop !== propToCheck && v[prop] !== selectedVariant.value[prop]) {
                    isValid = false;
                }
            });
            return isValid;
        })
        .map((v) => v[propToCheck]);
}
  
function getDistinctVariantProperty(
    property: string,
    checkAgainst: ProductProperty[],
    descriptionProperty: string = undefined,
): ProductPropertyOption[] {
    const props = uniqBy(
        product.value.variants.map((v) => ({
            value: v[property] !== null ? v[property] : (Dropdown as any).EMPTY_VALUE,
            description: descriptionProperty ? v[descriptionProperty] : null,
        })),
        (o) => o.value,
    );
  
    const filteredProps = props.filter((prop) => {
        return prop.value.length > 0 && prop.value !== "-" && prop.value !== "--";
    });
  
    if (filteredProps.length === 1 && filteredProps[0].value === (Dropdown as any).EMPTY_VALUE) {
        return [];
    }
  
    const valid = validOptions(property, checkAgainst);
    return filteredProps.filter((option) => valid.includes(option.value));
}
  
function getVariantIdFromUrl(): string {
    const id = QueryParamService.getParam(QueryParams.SelectedVariantId);
    return id;
}
  
function getVariantFromUrl() {
    // variant from url will always overrule variant.isDefault
    const id = getVariantIdFromUrl();
    const variant = product.value.variants.find((x) => x.id === id);
    return variant;
}
  
function getDefaultVariant() {
    const defaultVariant = product.value.variants.find((v) => v.isDefault);
    return defaultVariant || product.value.variants[0];
}
  
function findBestVariantCadidate(lastPropertyChanged: string, propertyValue: string) {
    const matchValue = (value: string, selected: string) => {
        const isMacthingEmpty = value === null && (selected === (Dropdown as any).EMPTY_VALUE || selected === "");
        const isMatchingValue = value === selected;
        return isMatchingValue ? true : isMacthingEmpty;
    };
  
    let candidates = product.value.variants.filter((v) => matchValue(v[lastPropertyChanged], propertyValue));
    Object.keys(prioritizedVariantFilter.value).forEach((propertyName) => {
        const entry = prioritizedVariantFilter.value[propertyName];
        if (entry.selectedValue) {
            const tempNextCandidates = candidates.filter((v) => matchValue(v[propertyName], entry.selectedValue));
            candidates = tempNextCandidates.length > 0 ? tempNextCandidates : candidates;
        }
    });
    selectVariant(candidates[0]);
}
  
function changeFilter(value: string, property: string) {
    prioritizedVariantFilter.value[property].selectedValue = value;
    findBestVariantCadidate(property, value);
}
  
function showVariantList() {
    toggleShowVariantList(true);
  
    TrackingService.event("Ecommerce", gaTrackingGoal.value, "Scroll to variant list - variant picker");
}
  
function trackVariant() {
    const useFirstPdpEventName = !hasChangedRoute.value && !hasTrackedVariant;
  
    if ((window as any).dataLayer) {
        const trackingProduct = {
            id: selectedVariant.value.sapId,
            price: (selectedVariant.value as PrivateVariantViewObject).rawPrice ? (selectedVariant.value as PrivateVariantViewObject).rawPrice : "",
            dimension3: getVariantIdFromUrl(),
        };
        (window as any).dataLayer.push({
            event: useFirstPdpEventName ? "firstpdp" : "pdp",
            ecommerce: {
                currencyCode: product.value.productTracking.currencyCode ? product.value.productTracking.currencyCode : "",
                detail: {
                    products: [trackingProduct],
                },
            },
        });
  
        hasTrackedVariant = true;
    }
}
  
function findRetailerButtonClicked() {
    TrackingService.event("Ecommerce", gaTrackingGoal.value, "Find dealer variant picker");
}

return { __sfc: true,product, selectedVariant, ExpiviComponentInitialized, initializeExpiviComponent, gaTrackingGoal, findRetailersUrl, hasChangedRoute, theme, breakpointIsActive, selectVariant, toggleShowVariantList, colors, options, prioritizedVariantFilter, hasTrackedVariant, productProperties, hasSplashText, getSplashIcon, onSelectedVariantChanged, showDeliveryTime, showReadyForShipment, showAddToBasketButton, showFindDealerButton, showGetQuoteButton, showDiscontinuedText, showSoldOutText, showPricingB2BButton, showAddToBasketB2BButton, showGetQuoteB2CButton, productPackshotColor, getDistinctColors, validOptions, getDistinctVariantProperty, getVariantIdFromUrl, getVariantFromUrl, getDefaultVariant, findBestVariantCadidate, changeFilter, showVariantList, trackVariant, findRetailerButtonClicked, PDPSwiper, ProductVariantSpec, GetCutsheetLink, ProductPropertySelector, ProductViewInAugmentedReality }
}

})